/**
 * Invoices entity config (11F.7) — drives EntityTable on the Invoices list.
 *
 * Closes three findings from the 2026-08-09/10 staging review, all of which
 * were really the same missing capability (the list had no server-side
 * paged contract):
 *   - no **Invoicee / Student Name** column   → `student_name` column below
 *   - no **search** by name or invoice number → EntityTable's search box,
 *     forwarded to the backend's merged prefix search (11F.7)
 *   - no **pagination**                        → page-number mode with an
 *     exact `total` from a Firestore count() aggregation
 *
 * Column keys double as the `sort` query-param value, so every key here
 * must be one the backend's `list_invoices_paged` sort map understands
 * (issue_date | due_date | status | total | number | student_name).
 *
 * Reference: `Invoices.png` (information architecture only — the visual
 * language is Candeza's own per D13).
 */

import { StatusBadge } from '@/components/common/Badge';
import type { EntityTableConfig } from '@/components/entity/types';
import type { InvoiceResponse } from '../api/billing';
import { formatMinorAmount } from './ui';
import { DateCell, MutedCell, NumericCell } from './billingEntityComponents';

export const invoicesTableConfig: EntityTableConfig<InvoiceResponse> = {
  entityName: 'invoice',
  apiPath: '/v1/invoices',
  routeBase: '/invoices',
  testIdPrefix: 'candeza-invoice',
  defaultSort: 'issue_date',
  defaultOrder: 'desc',
  // No default status filter: an admin opening Invoices wants the whole
  // book, not a pre-narrowed slice (unlike Students, where archived rows
  // are noise by default).
  defaultFilters: {},

  columns: [
    {
      key: 'number',
      header: 'Invoice #',
      isPrimary: true,
      sortable: true,
      helpKey: 'invoices.number',
      render: (inv) =>
        inv.number ?? <MutedCell>Draft — not yet issued</MutedCell>,
    },
    {
      key: 'student_name',
      header: 'Invoicee',
      sortable: true,
      helpKey: 'invoices.invoicee',
      render: (inv) =>
        // Empty when the backfill could not resolve the student (deleted
        // record). Render a dash rather than a blank cell so the row still
        // reads as intentional — the Data Quality queue carries the flag.
        inv.student_name ? inv.student_name : <MutedCell>Unknown student</MutedCell>,
    },
    {
      key: 'issue_date',
      header: 'Issued',
      sortable: true,
      helpKey: 'invoices.issue_date',
      render: (inv) => <DateCell value={inv.issue_date} />,
    },
    {
      key: 'due_date',
      header: 'Due',
      sortable: true,
      hideOnMobile: true,
      helpKey: 'invoices.due_date',
      render: (inv) => <DateCell value={inv.due_date} />,
    },
    {
      key: 'total',
      header: 'Amount',
      sortable: true,
      helpKey: 'invoices.total',
      render: (inv) => (
        <NumericCell>{formatMinorAmount(inv.total, inv.currency)}</NumericCell>
      ),
    },
    {
      key: 'amount_due',
      header: 'Balance',
      hideOnMobile: true,
      helpKey: 'invoices.amount_due',
      render: (inv) => (
        <NumericCell
          color={inv.amount_due > 0 ? '#b45309' : '#15803d'}
          bold={inv.amount_due > 0}
        >
          {formatMinorAmount(inv.amount_due, inv.currency)}
        </NumericCell>
      ),
    },
    {
      key: 'items',
      header: 'Items',
      hideOnMobile: true,
      helpKey: 'invoices.items',
      // Line items display by SUBJECT NAME, not id (D2 / 11E.4). Falls back
      // to the stored description when a line has no subject (ad-hoc fees
      // like registration) or the subject no longer resolves.
      render: (inv) => {
        const labels = inv.line_items.map(
          (li) => li.subject_name || li.description,
        );
        if (labels.length === 0) return <MutedCell>—</MutedCell>;
        const shown = labels.slice(0, 2).join(', ');
        return labels.length > 2 ? `${shown} +${labels.length - 2}` : shown;
      },
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      helpKey: 'invoices.status',
      render: (inv) => <StatusBadge status={inv.status} />,
    },
  ],

  filters: [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      helpKey: 'invoices.filter.status',
      options: [
        { value: 'all', label: 'All statuses' },
        { value: 'draft', label: 'Draft' },
        { value: 'issued', label: 'Issued' },
        { value: 'partially_paid', label: 'Partially paid' },
        { value: 'paid', label: 'Paid' },
        { value: 'void', label: 'Void' },
      ],
    },
  ],

  // No 'archive': an issued invoice is never hard-deleted (invariant I-4).
  // Voiding is a status transition with a mandatory reason and lives in the
  // detail page's Actions menu, behind a confirm.
  rowActions: ['view'],
};
